import { useTranslation } from "react-i18next";
import { ReactComponent as RequirementCategoryIcon } from "../../assets/icons/ProposalsListModal/RequirementCategoryIcon.svg";
import { ReactComponent as AddressIcon } from "../../assets/icons/ProposalDisplayModal/AddressIcon.svg";
import { ReactComponent as WebIcon } from "../../assets/icons/ProposalDisplayModal/WebIcon.svg";
import { ReactComponent as SendIcon } from "../../assets/icons/ProposalDisplayModal/SendIcon.svg";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import FileDisplay from "../../modals/ProposalDisplayModal/FileDisplay.jsx";
import AssetImage from "../AssetImage";
import Caintra_Logo_03 from "../../assets/Caintra_Logo_03.png";
import { getCompanySizeOptions } from "../../data/selectOptions";
import { getFileNameFromUrl } from "../../logic/proposal/index.js";
import { getAssetFileName, getSelectedOption } from "../../utils/common.js";

/**
 * Proposal description, company, quote, and approve/star actions.
 * Callers pass their own CSS module so the page and the modal keep their styles.
 * Address markup keeps the page's newline before the town comma; browsers collapse it.
 * @param {Object} styles CSS module from the page or the modal
 * @param {Object} proposal Proposal being reviewed
 * @param {Object|null} company Company loaded for the proposing user
 * @param {Object|null} sector Economic sector for that company
 * @param {boolean} isStarred Current highlight flag
 * @param {Function} onQuoteDownload Downloads the quote file
 * @param {Function} onBrochureDownload Downloads the company brochure
 * @param {Function} onUnstar Clears the highlight
 * @param {Function} onStar Sets the highlight
 * @param {Function} onReject Page no-op, or the modal's reject dialog opener
 * @param {Function} onApprove Approves and leaves the page or closes the modal
 */
const ProposalDetailsContent = ({
  styles,
  proposal,
  company,
  sector,
  neighborhood,
  zipCode,
  town,
  state,
  country,
  isStarred,
  onQuoteDownload,
  onBrochureDownload,
  onUnstar,
  onStar,
  onReject,
  onApprove,
}) => {
  const { t } = useTranslation();
  const companySizeOptions = getCompanySizeOptions(t);
  const rejectDisabled =
    proposal.aprobacionPendiente === false && proposal.aprobado === false;

  return (
    <>
      <div className={styles.detailSection}>
        <div className={styles.companyInfo}>
          {company?.logo && (
            <AssetImage
              src={company.logo || Caintra_Logo_03}
              alt={t("common.companyLogoFor", {
                name: company.nombreComercial,
              })}
              className={styles.companyLogo}
            />
          )}
          <p className={styles.companyName}>{company?.nombreComercial}</p>
        </div>
      </div>

      <div className={styles.proposalDetails}>
        {proposal.razonDeRechazo && (
          <div className={styles.detailSection}>
            <h3>{t("components.proposalDisplayModal.rejectReason")}</h3>
            <p>{proposal.razonDeRechazo}</p>
          </div>
        )}

        <div className={styles.detailSection}>
          <h3>{t("components.proposalDisplayModal.description")}</h3>
          <p>{proposal.descripcion}</p>
        </div>

        <div className={styles.detailsRow}>
          <div className={styles.detailSection}>
            <h3>{t("components.proposalDisplayModal.deliveryTime")}</h3>
            <p>{proposal.tiempoDeEntrega}</p>
          </div>

          <div className={styles.detailSection}>
            <h3>{t("components.proposalDisplayModal.cost")}</h3>
            <p>${proposal.costo} MXN</p>
          </div>
        </div>

        <div className={styles.detailSection}>
          <h3>{t("components.proposalDisplayModal.quote")}</h3>
          <FileDisplay
            fileUrl={proposal.cotizacion}
            fileName={getFileNameFromUrl(proposal.cotizacion)}
            onDownload={onQuoteDownload}
          />
        </div>

        <div className={styles.divider}></div>

        <div className={styles.detailSection}>
          <p className={styles.companyName}>{company?.nombreComercial}</p>
        </div>

        <div className={styles.categoryContainer}>
          <div className={styles.categoryBox}>
            <RequirementCategoryIcon />
            <span className={styles.categoryText}>
              {t("components.proposalDisplayModal.size")}:{" "}
              {getSelectedOption(companySizeOptions, company?.tamanoDeLaEmpresa)
                ?.label || t("common.notAvailable")}
            </span>
          </div>
          <div className={styles.categoryBox}>
            <RequirementCategoryIcon />
            <span className={styles.categoryText}>
              {t("components.proposalDisplayModal.sector")}: {sector?.nombre}
            </span>
          </div>
        </div>

        <div className={styles.addressContainer}>
          <AddressIcon className={styles.addressIcon} />
          <span className={styles.addressText}>
            {company?.direccion?.[1]?.calle},{" "}
            {company?.direccion?.[1]?.numeroExterior}{" "}
            {t("components.proposalDisplayModal.neighborhood")}{" "}
            {neighborhood?.nombre}{" "}
            {t("components.proposalDisplayModal.zipCode")} {zipCode?.codigo}
            , {town?.nombre} {state?.nombre}, {country?.nombre}
          </span>
        </div>

        <div className={styles.websiteContainer}>
          <WebIcon className={styles.webIcon} />
          <a
            href={company?.paginaInternet}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.websiteText}
          >
            {company?.paginaInternet}
          </a>
        </div>

        <div className={styles.detailSection}>
          <FileDisplay
            fileUrl={company?.brochure}
            fileName={getAssetFileName(company?.brochure)}
            onDownload={onBrochureDownload}
          />
        </div>

        <div className={styles.starSection}>
          {isStarred ? (
            <StarIcon className={styles.starIcon} onClick={onUnstar} />
          ) : (
            <StarOutlineIcon className={styles.starIcon} onClick={onStar} />
          )}
          <span className={styles.starText}>
            {t("components.proposalDisplayModal.addFavorite")}
          </span>
        </div>

        <div className={styles.modalActions}>
          <button
            className={styles.rejectButton}
            onClick={onReject}
            disabled={rejectDisabled}
          >
            {t("components.proposalDisplayModal.rejectButton")}
          </button>
          <button className={styles.acceptButton} onClick={onApprove}>
            <SendIcon className={styles.sendIcon} />
            {t("components.proposalDisplayModal.acceptButton")}
          </button>
        </div>
      </div>
    </>
  );
};

export default ProposalDetailsContent;
